import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../Utils/axiosInstance";
import { persistReducer } from 'redux-persist';

const baseUrl = process.env.REACT_APP_BASE_URL;
import storage from 'redux-persist/lib/storage/session';

const persistConfig = {
  key: 'savedAddressDetails',
  storage,
};

// Async thunks
export const getAllSavedAddress = createAsyncThunk(
  "savedAddressDetails/getAllSavedAddress",
  async ({ shopperRef, sessionToken, checkoutToken }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/checkout/sessions/${checkoutToken}/shoppers/${shopperRef}/addresses`,
        {
          headers: { Authorization: `Session ${sessionToken}` },
        }
      );
      return response.data;
    } catch (error) {
      console.log('address error ', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const addSavedAddress = createAsyncThunk(
  "savedAddressDetails/addSavedAddress",
  async ({ shopperRef, payload, sessionToken, checkoutToken }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}/checkout/sessions/${checkoutToken}/shoppers/${shopperRef}/addresses`,
        payload,
        {
          headers: { Authorization: `Session ${sessionToken}` },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const updateSavedAddress = createAsyncThunk(
  "savedAddressDetails/updateSavedAddress",
  async ({ shopperRef, payload, sessionToken, labelType, labelName, checkoutToken }, { rejectWithValue }) => {
    try {
      let url = `${baseUrl}/checkout/sessions/${checkoutToken}/shoppers/${shopperRef}/addresses/${labelType}`;
      if (labelName) url += `-${labelName}`;
      const response = await axiosInstance.put(url, payload, {
        headers: { Authorization: `Session ${sessionToken}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const deleteSavedAddress = createAsyncThunk(
  "savedAddressDetails/deleteSavedAddress",
  async ({ shopperRef, sessionToken, addressRef, checkoutToken }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `${baseUrl}/checkout/sessions/${checkoutToken}/shoppers/${shopperRef}/addresses/${addressRef}`,
        {
          headers: { Authorization: `Session ${sessionToken}` },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Initial state
const initialState = {
  allSavedAddresses: [],
  allSavedAddressesLoading: false,
  allSavedAddressesErrorMessage: "",

  savedAddress: [],
  savedAddressesLoading: false,
  savedAddressesErrorMessage: "",
  isConflictedEntity: false,
  conflictedLabelName: "",

  updatedSavedAddressData: [],
  updatedSavedAddressesLoading: false,
  updatedSavedAddressesErrorMessage: "",

  deleteSavedAddressData: [],
  deleteSavedAddressesLoading: false,
  deleteSavedAddressesErrorMessage: "",

  showBackButton: false,
  addressButtonLoading: false,
};

// Slice
const savedAddressDetailsSlice = createSlice({
  name: "savedAddressDetails",
  initialState,
  reducers: {
    showGoBackButton(state, action) {
      state.showBackButton = action.payload;
    },
    removeAllSavedAddress(state) {
      state.allSavedAddresses = [];
    },
    handleAddressConflictModal(state, action) {
      state.isConflictedEntity = action.payload;
      state.conflictedLabelName = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // GET
      .addCase(getAllSavedAddress.pending, (state) => {
        state.allSavedAddressesLoading = true;
        state.allSavedAddressesErrorMessage = "";
      })
      .addCase(getAllSavedAddress.fulfilled, (state, action) => {
        state.allSavedAddressesLoading = false;
        state.allSavedAddresses = action.payload;
      })
      .addCase(getAllSavedAddress.rejected, (state, action) => {
        state.allSavedAddressesLoading = false;
        state.allSavedAddresses = [];
        state.allSavedAddressesErrorMessage = action.payload || "Failed to load addresses.";
      })

      // ADD
      .addCase(addSavedAddress.pending, (state) => {
        state.savedAddressesLoading = true;
        state.addressButtonLoading = true;
        state.isConflictedEntity = false;
        state.savedAddressesErrorMessage = "";
      })
      .addCase(addSavedAddress.fulfilled, (state, action) => {
        state.savedAddressesLoading = false;
        state.addressButtonLoading = false;
        state.savedAddress = action.payload;
        state.isConflictedEntity = false;
        state.conflictedLabelName = "";
      })
      .addCase(addSavedAddress.rejected, (state, action) => {
        state.savedAddressesLoading = false;
        state.addressButtonLoading = false;
        state.savedAddress = [];
        const payload = action.payload || {};
        state.savedAddressesErrorMessage = payload.message || "Error adding address";

        if (payload.errorCode === "conflicted_entity") {
          state.isConflictedEntity = true;
          const labelNameMatch = payload.message?.match(/labelName:\s*(.*)$/);
          if (labelNameMatch?.[1]) {
            state.conflictedLabelName = labelNameMatch[1];
          }
        } else {
          state.isConflictedEntity = false;
        }
      })

      // UPDATE
      .addCase(updateSavedAddress.pending, (state) => {
        state.updatedSavedAddressesLoading = true;
        state.addressButtonLoading = true;
        state.updatedSavedAddressesErrorMessage = "";
      })
      .addCase(updateSavedAddress.fulfilled, (state, action) => {
        state.updatedSavedAddressesLoading = false;
        state.addressButtonLoading = false;
        state.updatedSavedAddressData = action.payload;
      })
      .addCase(updateSavedAddress.rejected, (state, action) => {
        state.updatedSavedAddressesLoading = false;
        state.addressButtonLoading = false;
        state.updatedSavedAddressData = [];
        state.updatedSavedAddressesErrorMessage = action.payload || "Error updating address";
      })

      // DELETE
      .addCase(deleteSavedAddress.pending, (state) => {
        state.deleteSavedAddressesLoading = true;
        state.deleteSavedAddressesErrorMessage = "";
      })
      .addCase(deleteSavedAddress.fulfilled, (state, action) => {
        state.deleteSavedAddressesLoading = false;
        state.deleteSavedAddressData = action.payload;
      })
      .addCase(deleteSavedAddress.rejected, (state, action) => {
        state.deleteSavedAddressesLoading = false;
        state.deleteSavedAddressData = [];
        state.deleteSavedAddressesErrorMessage = action.payload || "Error deleting address";
      });
  },
});

// Selectors & Exports
export const selectSavedAddresses = (state) => state.savedAddressDetails.allSavedAddresses;
export const persistedSavedAddressSliceReducer = persistReducer(
  persistConfig,
  savedAddressDetailsSlice.reducer
);

export const {
  showGoBackButton,
  removeAllSavedAddress,
  handleAddressConflictModal,
} = savedAddressDetailsSlice.actions;

export default savedAddressDetailsSlice.reducer;


